import { Component } from "@theme/component";
import { onDocumentLoaded, setHeaderMenuStyle, calculateHeaderGroupHeight } from "@theme/utilities";

/**
 * `OverflowMinimumEvent` in overflow-list.js extends `Event` rather than `CustomEvent`, so it is
 * described structurally here instead of importing the class — the typedef exists purely to type
 * the listener and must not pull overflow-list.js into this bundle.
 * @typedef {Event & { detail: { minimumReached: boolean } }} OverflowMinimumEvent
 */

/**
 * Minimum distance, in pixels, a scroll has to travel in one direction before the header stack
 * reacts. Without it a trackpad's jitter or an iOS rubber-band flaps the pill in and out.
 */
const HEADER_REVEAL_HYSTERESIS = 8;

/**
 * Attribute set on `<body>` while the stack is scrolled out of view. Both fixed layers of the
 * stack — the pill in `sections/header.liquid` and the USP banner in
 * `sections/header-announcements.liquid` — translate off this one attribute so they travel
 * together; the transforms themselves live in those two section stylesheets.
 */
const HEADER_HIDDEN_ATTRIBUTE = "data-header-hidden";

/**
 * A custom element that manages the site header.
 *
 * @extends {Component}
 */

class HeaderComponent extends Component {
  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * Whether the header stack is currently translated out of view
   * @type {boolean}
   */
  #headerHidden = false;

  /**
   * The scrollTop the hide/reveal behaviour last acted on
   * @type {number}
   */
  #revealScrollTop = 0;

  /**
   * RAF ID for the hide/reveal scroll handler
   * @type {number | null}
   */
  #revealRafId = null;

  /**
   * The header pill is `position: fixed`, so it contributes no flow height and measuring it
   * tells consumers nothing useful. `--header-height` is therefore owned by CSS in
   * header.liquid (pill height + its top offset, consumed as top *clearance*), and this
   * observer only clears any stale inline override left on `body` by the bootstrap script in
   * theme.liquid — for instance when the theme editor re-adds a previously hidden header.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    document.body.style.removeProperty("--header-height");

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Handles the overflow minimum event from the header menu
   * @param {Event} event
   */
  #handleOverflowMinimum = (event) => {
    const { minimumReached } = /** @type {OverflowMinimumEvent} */ (event).detail;
    this.#updateMenuVisibility(minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleRevealScroll = () => {
    if (this.#revealRafId !== null) return;

    this.#revealRafId = requestAnimationFrame(() => {
      this.#revealRafId = null;
      this.#updateRevealState();
    });
  };

  /**
   * Scrolls the header stack out of view on the way down the page and brings it back on the first
   * upward scroll, once the page has moved past the stack's own footprint.
   */
  #updateRevealState = () => {
    const scrollTop = Math.max(document.scrollingElement?.scrollTop ?? 0, 0);

    /*
     * Two cases where the stack holds its current state, both re-baselining as they bail so the
     * distance travelled while frozen cannot later be read as one big scroll in one direction:
     *
     * - A scroll-locked `<html>` means a drawer or a modal owns the viewport. That includes the
     *   navigation, drawer and dropdown panel alike: both live inside this element and would be
     *   dragged offscreen with it. Horizon's `dialog.js` and `nav-menu.js` own the attribute.
     * - Focus inside the header means a keyboard user arrow-scrolling would otherwise watch the
     *   control they are on leave the viewport.
     */
    if (document.documentElement.hasAttribute("scroll-lock") || this.contains(document.activeElement)) {
      this.#revealScrollTop = scrollTop;
      return;
    }

    const delta = scrollTop - this.#revealScrollTop;

    /*
     * Read live rather than cached: `--header-height` covers the pill's top offset, which steps at
     * the xl breakpoint and again whenever the USP banner is dismissed. This runs at most once per
     * animation frame, and within the callback it is a read that precedes every write.
     *
     * header.liquid registers `--header-height` with `@property ... syntax: '<length>'`, which is
     * what makes this parse: an unregistered property would hand back its unevaluated `calc()`.
     */
    const stackHeight = parseFloat(getComputedStyle(document.body).getPropertyValue("--header-height")) || 0;

    // Inside its own footprint the stack always shows — hiding here would animate it out of space
    // no page content has reached yet.
    if (scrollTop <= stackHeight) {
      this.#setHeaderHidden(false);
      this.#revealScrollTop = scrollTop;
      return;
    }

    // Deliberately leaves #revealScrollTop alone, so a slow scroll still accumulates towards the
    // threshold instead of resetting its baseline every frame.
    if (Math.abs(delta) < HEADER_REVEAL_HYSTERESIS) return;

    this.#setHeaderHidden(delta > 0);
    this.#revealScrollTop = scrollTop;
  };

  /**
   * Reveals the stack when focus lands inside it, so a keyboard user tabbing in from the top of
   * the document never lands on an offscreen control.
   */
  #handleRevealFocusIn = () => {
    this.#setHeaderHidden(false);
    this.#revealScrollTop = Math.max(document.scrollingElement?.scrollTop ?? 0, 0);
  };

  /**
   * Reveals the stack when a drawer or modal takes the viewport, so the panel docks under a
   * visible pill rather than under the empty clearance a hidden one leaves behind.
   */
  #scrollLockObserver = new MutationObserver(() => {
    if (document.documentElement.hasAttribute("scroll-lock")) {
      this.#setHeaderHidden(false);
    }
    this.#revealScrollTop = Math.max(document.scrollingElement?.scrollTop ?? 0, 0);
  });

  /**
   * @param {boolean} hidden - Whether the stack should be translated out of view
   */
  #setHeaderHidden = (hidden) => {
    if (hidden === this.#headerHidden) return;

    this.#headerHidden = hidden;
    document.body.toggleAttribute(HEADER_HIDDEN_ATTRIBUTE, hidden);
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);

    /*
     * The section is re-rendered on hydration and by the theme editor, so this instance starts
     * from the live scroll position with the stack shown rather than inheriting whatever the
     * previous one left on `body`.
     */
    this.#revealScrollTop = Math.max(document.scrollingElement?.scrollTop ?? 0, 0);
    document.body.removeAttribute(HEADER_HIDDEN_ATTRIBUTE);
    document.addEventListener("scroll", this.#handleRevealScroll, { passive: true });
    this.addEventListener("focusin", this.#handleRevealFocusIn);
    this.#scrollLockObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["scroll-lock"]
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);

    document.removeEventListener("scroll", this.#handleRevealScroll);
    this.removeEventListener("focusin", this.#handleRevealFocusIn);
    this.#scrollLockObserver.disconnect();
    if (this.#revealRafId !== null) {
      cancelAnimationFrame(this.#revealRafId);
      this.#revealRafId = null;
    }
    // A hidden stack must not leave the attribute behind: the USP banner is a separate section
    // that survives this one being removed, and would stay translated offscreen.
    document.body.removeAttribute(HEADER_HIDDEN_ATTRIBUTE);

    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("header-component");
  const headerGroup = /** @type {HTMLElement | null} */ (document.querySelector("#header-group"));

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    // Always measure the whole group rather than summing the entries in the callback: a
    // ResizeObserver batch only contains the elements that actually changed, so reducing
    // over it would drop every sibling's height and leave the group height too short.
    const updateHeaderGroupHeight = () => {
      // Re-query the header rather than reusing the reference captured on load, which goes
      // stale whenever the header section is re-rendered.
      const currentHeader = /** @type {HTMLElement | null} */ (document.querySelector("#header-component"));
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(calculateHeaderGroupHeight(currentHeader, headerGroup));
      document.body.style.setProperty("--header-group-height", `${roundedHeaderGroupHeight}px`);
    };

    const resizeObserver = new ResizeObserver(updateHeaderGroupHeight);

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }

          // A removed section fires no resize on the survivors, so recompute here too.
          updateHeaderGroupHeight();
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
